const { fetchJSON, getDefaultRequestConfig, getElement } = window.BAO.Utils

const defaultSectionsUrl = () => {
  const appProxyUrls = [`/apps/`, `/a/`, `/tools/`, `/community/`]

  if (appProxyUrls.some((url) => window.location.pathname.includes(url))) {
    return `/`
  }

  return window.location.pathname
}

/** @const {string} The context in which we want the sections to be rendered in */
const DEFAULT_SECTIONS_URL = defaultSectionsUrl()

/**
 * @typedef {Object} PotentialCartItem
 *
 * @property {number} id The variant's unique ID
 * @property {number} quantity The quantity of items to be added to the cart
 * @property {Object} [properties] Line item property key/values
 */

/**
 * @typedef {Object} BaoCartSectionOptions
 *
 * @property {string[]} [sections] - The sections we want rendered alongside any cart
 *   changes
 * @property {string} [sectionsUrl] - The context in which we want the sections to be
 *   rendered in in
 */

/**
 * @typedef {BaoCartSectionOptions} BaoCartSectionOptionsWithAttributes
 *
 * @property {Object} [attributes] Key/values that we want to add to the cart attributes
 */

/**
 * @typedef {BaoCartSectionOptions} BaoCartSectionOptionsWithForm
 *
 * @property {HTMLFormElement} [form] The form that has added the item
 */

/**
 * By Association Only's cart helper methods
 */
class BaoCart {
  /**
   *
   */
  async getState () {}

  /**
   * @param {HTMLFormElement} form
   * @param {BaoCartSectionOptionsWithAttributes} options Optional values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItemFromForm (
    form,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const formData = new FormData(form)
    formData.append('sections', sections)
    formData.append('sections_url', sectionsUrl)

    window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM, {
      form,
      formData,
    })

    const config = getDefaultRequestConfig('javascript')
    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    delete config.headers['Content-Type']
    config.body = formData

    try {
      const state = await fetchJSON(`${window.routes.cartAddUrl}`, config)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
        {
          form,
          formData,
          state,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      this.handleAddToCartError(err)

      const pushCartSections = window.BAO.sectionRenderer.pushCartSections

      if (!pushCartSections || !pushCartSections.length) return

      window.BAO.sectionRenderer
        .fetch(pushCartSections.map((s) => s.section))
        .then((htmlSections) => {
          const updatedSections = pushCartSections.map((section) => ({
            ...section,
            html: htmlSections[section.section],
          }))

          window.BAO.sectionRenderer.renderMultiple(updatedSections)

          const cartCountHTML = htmlSections['push-cart-count']

          if (cartCountHTML) {
            window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
              state: {
                sections: {
                  'push-cart-count': cartCountHTML,
                },
              },
            })
          }
        })
        .catch((err) => {
          console.error('Failed to fetch cart sections:', err)
        })
    }
  }

  /**
   * @param {PotentialCartItem[]|PotentialCartItem} items
   * @param {BaoCartSectionOptionsWithAttributes & BaoCartSectionOptionsWithForm} options Optional
   *   values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItem (
    items,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
      attributes = {},
      form,
    } = {},
  ) {
    if (!Array.isArray(items)) {
      items = [items]
    }

    window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.BEFORE_ADD, {
      items,
      attributes,
      form,
    })

    const body = {
      items,
      attributes,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartAdd(body)

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.AFTER_ADD, {
        items,
        state,
        form,
      })

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      this.handleAddToCartError(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The amount of the item we want change the line item to. Can be 0.
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateQuantity (
    key,
    quantity,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_ITEM_QUANTITY,
      {
        key,
        quantity,
      },
    )
    const body = {
      id: key,
      quantity,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartChange(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_ITEM_QUANTITY,
        {
          state,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      this.handleAddToCartError(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItem (
    key,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM,
      { key },
    )
    const body = {
      id: key,
      quantity: 0,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartChange(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
        {
          state,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @param {string[]} keys The key of the line item we want to change
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItems (
    keys,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM,
      { keys },
    )

    const body = {
      updates: Object.fromEntries(keys.map((k) => [k, 0])),
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
        {
          state,
        },
      )
      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The quantity of items to be added to the cart. If this isn't set then Shopify will reset it back to 1
   * @param {object} properties An object of key/value pairs you want to update
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateItemProperties (
    key,
    quantity,
    properties,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const body = { id: key, properties, quantity }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      return this.cartChange(body)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {object} attributes An object of key/value pairs you want to update
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateCartAttributes (
    attributes,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_CART_ATTRIBUTES,
      {
        attributes,
      },
    )

    const body = { attributes }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_CART_ATTRIBUTES,
        {
          attributes,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {string} note The note we want to set
   *
   * @returns {Promise<Object>}
   */
  async updateNote (note) {
    window.BAO.dispatchEvent(window.BAO.EVENTS.CART_NOTE.BEFORE_CHANGE, {
      note,
    })

    try {
      const state = await this.cartUpdate({ note })

      window.BAO.dispatchEvent(window.BAO.EVENTS.CART_NOTE.AFTER_CHANGE, {
        state,
        note,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {string} discount A comma separated list of discount codes
   *
   * @returns {Promise<Object>}
   */
  async updateDiscount (discount) {
    window.BAO.dispatchEvent(window.BAO.EVENTS.CART_DISCOUNT.BEFORE_CHANGE, {
      discount,
    })

    try {
      const state = await this.cartUpdate({ discount })

      window.BAO.dispatchEvent(window.BAO.EVENTS.CART_DISCOUNT.AFTER_CHANGE, {
        state,
        discount,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  handleAddToCartError (res) {
    /**
     * @type {PushCart}
     */
    const pushCart = getElement('push-cart')
    if (!pushCart) return

    pushCart.handleError(res)
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartAdd (body) {
    return fetchJSON(`${window.routes.cartAddUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartChange (body) {
    return fetchJSON(`${window.routes.cartChangeUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartUpdate (body) {
    return fetchJSON(`${window.routes.cartUpdateUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }
}

window.BAO.Cart = new BaoCart()

/**
 * @typedef {Object} CartImage
 * @property {string} url - Full URL to the image
 * @property {number} aspect_ratio - Width/height ratio
 * @property {string} alt - Alt text for the image
 * @property {number} height - Image height in pixels
 * @property {number} width - Image width in pixels
 */

/**
 * @typedef {Object} SellingPlanOption
 * @property {string} name - Option name (e.g., "Delivery Frequency")
 * @property {number} position - Display position
 * @property {string} value - Option value (e.g., "Month")
 */

/**
 * @typedef {Object} SellingPlan
 * @property {number} id - Selling plan ID
 * @property {string} name - Display name
 * @property {string} description - Plan description
 * @property {SellingPlanOption[]} options - Plan options
 * @property {boolean} recurring_deliveries - Whether deliveries recur
 */

/**
 * @typedef {Object} SellingPlanAllocation
 * @property {number} price - Price under this selling plan
 * @property {number} compare_at_price - Original price before plan discount
 * @property {number} per_delivery_price - Price per delivery
 * @property {SellingPlan} selling_plan - Selling plan details
 */

/**
 * @typedef {Object} OptionWithValue
 * @property {string} name - Option name (e.g., "Color", "Size")
 * @property {string} value - Selected value for this option
 */

/**
 * @typedef {Object} LineItemDiscount
 * @property {number} amount - Discount amount in cents
 * @property {string} title - Discount title/code
 */

/**
 * @typedef {Object} LineLevelDiscountAllocation
 * @property {number} amount - Allocated discount amount
 * @property {string} discount_application_index - Index of the discount application
 */

/**
 * @typedef {Object} QuantityRule
 * @property {number} min - Minimum quantity allowed
 * @property {number|null} max - Maximum quantity allowed (null = no limit)
 * @property {number} increment - Quantity increment step
 */

/**
 * @typedef {Object} CartLineItem
 * @property {number} id - Variant ID
 * @property {Object.<string, any>} properties - Custom line item properties
 * @property {number} quantity - Quantity in cart
 * @property {number} variant_id - Same as id
 * @property {string} key - Unique line item key (changes when properties/discounts change)
 * @property {string} title - Full product title with variant options
 * @property {number} price - Unit price in cents
 * @property {number} original_price - Original unit price before discounts
 * @property {number} presentment_price - Price in decimal format for display
 * @property {number} discounted_price - Unit price after line-level discounts
 * @property {number} line_price - Total line price after discounts
 * @property {number} original_line_price - Original line total before discounts
 * @property {number} total_discount - Total discount amount for this line
 * @property {LineItemDiscount[]} discounts - Applied discounts
 * @property {string|null} sku - SKU code
 * @property {number} grams - Weight in grams
 * @property {string} vendor - Product vendor
 * @property {boolean} taxable - Whether item is taxable
 * @property {number} product_id - Parent product ID
 * @property {boolean} product_has_only_default_variant - Single variant product flag
 * @property {boolean} gift_card - Whether this is a gift card
 * @property {number} final_price - Final unit price after all discounts
 * @property {number} final_line_price - Final line total after all discounts
 * @property {string} url - Product URL with variant parameter
 * @property {CartImage} featured_image - Main product image
 * @property {string} image - Legacy image URL (same as featured_image.url)
 * @property {string} handle - Product handle/slug
 * @property {boolean} requires_shipping - Whether item requires shipping
 * @property {string} product_type - Product type/category
 * @property {string} product_title - Product title without variant
 * @property {string|null} product_description - Product description
 * @property {string} variant_title - Variant options as string
 * @property {string[]} variant_options - Array of variant option values
 * @property {OptionWithValue[]} options_with_values - Structured variant options
 * @property {LineLevelDiscountAllocation[]} line_level_discount_allocations - Line-level discount allocations
 * @property {number} line_level_total_discount - Total line-level discount amount
 * @property {QuantityRule} quantity_rule - Quantity constraints for this item
 * @property {boolean} has_components - Whether item has bundled components
 * @property {SellingPlanAllocation} [selling_plan_allocation] - Subscription details if applicable
 */

/**
 * @typedef {Object} CartLevelDiscountApplication
 * @property {string} type - Type of discount (e.g., "discount_code", "automatic")
 * @property {string} key - Unique discount key/ID
 * @property {string} title - Display title/code name
 * @property {string|null} description - Discount description
 * @property {string} value - Discount value as string (e.g., "80.0" for 80%)
 * @property {string} created_at - ISO timestamp when discount was applied
 * @property {string} value_type - "percentage" or "fixed_amount"
 * @property {string} allocation_method - How discount is allocated (e.g., "across")
 * @property {string} target_selection - What items are targeted (e.g., "all")
 * @property {string} target_type - Type of target (e.g., "line_item")
 * @property {number} total_allocated_amount - Total discount amount allocated in cents
 */

/**
 * @typedef {Object} DiscountCode
 * @property {string} code - The discount code
 * @property {boolean} applicable - Whether the code is currently applicable
 */

/**
 * @typedef {Object} ShopifyCart
 * @property {string} token - Unique cart token
 * @property {string} note - Cart note (empty string if none)
 * @property {Object.<string, string>} attributes - Custom cart attributes
 * @property {number} original_total_price - Total before discounts (in cents)
 * @property {number} total_price - Final total after discounts (in cents)
 * @property {number} total_discount - Total discount amount (in cents)
 * @property {number} total_weight - Total weight in grams
 * @property {number} item_count - Total quantity of all items
 * @property {CartLineItem[]} items - Array of cart line items
 * @property {boolean} requires_shipping - Whether any items require shipping
 * @property {string} currency - Three-letter currency code (e.g., "GBP", "USD")
 * @property {number} items_subtotal_price - Subtotal of items before cart-level discounts
 * @property {CartLevelDiscountApplication[]} cart_level_discount_applications - Cart-level discounts
 * @property {DiscountCode[]} discount_codes - Applied discount codes
 */
